import { AfterViewInit, Directive, ElementRef, Renderer2 } from '@angular/core';
import { LinkTarget } from '../pipes/link-target/link-target.enum';
import { SettingsService } from '../../api/services/setting.service';
import { isInIframe } from '../helpers/maporium.validators';
import { environment } from '../../../environments/environment';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[datify]',
  standalone: true
})
export class DatatifyDirective implements AfterViewInit{

  static onLinkClick(event: Event | null, url: string, linkTarget: LinkTarget) {
    event?.preventDefault();
    // Inside a smaplet, a hyperlink to another smap must not dump the viewer on
    // the sign-in screen (they usually have no account). If it points to a
    // publicly embeddable smap, launch it as a full-window smaplet instead;
    // otherwise fall back to opening it in the application as before.
    const smapMapId = isInIframe() ? DatatifyDirective.extractSmapMapId(url) : null;
    if (smapMapId) {
      DatatifyDirective.launchSmapletOrFallback(smapMapId, url, linkTarget);
      return;
    }
    DatatifyDirective.openWithTarget(url, linkTarget);
  }

  private static openWithTarget(url: string, linkTarget: LinkTarget) {
    const windowName = '_singleResourceWindow';
    const tabName = '_singleResourceTab';
    switch (linkTarget) {
      case LinkTarget.SingleWindow:
        window.open(url, windowName, 'width=800,height=600');
        break;
      case LinkTarget.SingleTab:
        window.open(url, tabName);
        break;
      case LinkTarget.MultiWindow:
        window.open(url, '_blank', 'noopener,noreferrer,width=800,height=600');
        break;
      case LinkTarget.MultiTab:
        window.open(url, '_blank');
        break;
      default:
        console.error('Unknown LinkTarget:', linkTarget);
        window.open(url, '_blank');
        break;
    }
  }

  /**
   * Returns the smap id when `rawUrl` is a same-origin link to a Maporium smap
   * (either `/dashboard/map/:id` or `/dashboard?mapId=:id`), else null.
   */
  private static extractSmapMapId(rawUrl: string): string | null {
    try {
      const url = new URL(rawUrl, location.origin);
      if (url.origin !== location.origin) return null;
      const queryId = url.searchParams.get('mapId');
      if (queryId) return queryId;
      const match = url.pathname.match(/\/dashboard\/map\/([^/?#]+)/);
      return match ? match[1] : null;
    } catch {
      return null;
    }
  }

  /**
   * Probe the anonymous public endpoint (200 only when the smap is public AND
   * embeddable) and, if it resolves, present the smap as a full-window smaplet.
   * The window is opened synchronously so the popup blocker allows it, then its
   * contents are decided once the probe returns.
   */
  private static launchSmapletOrFallback(mapId: string, url: string, linkTarget: LinkTarget) {
    const smapletWindow = window.open(
      '',
      '_blank',
      'resizable=yes,scrollbars=yes,width=1200,height=800'
    );
    fetch(`${environment.apiUrl}/public/map/${encodeURIComponent(mapId)}`)
      .then(res => (res.ok ? res.json() : null))
      .then(map => {
        if (map) {
          DatatifyDirective.writeSmapletWindow(smapletWindow, mapId, map.name);
        } else {
          DatatifyDirective.fallbackToApp(smapletWindow, url, linkTarget);
        }
      })
      .catch(() => DatatifyDirective.fallbackToApp(smapletWindow, url, linkTarget));
  }

  /** Render the smap inside an iframe so it loads in smaplet (read) mode. */
  private static writeSmapletWindow(smapletWindow: Window | null, mapId: string, name: string) {
    if (!smapletWindow) return;
    // A smaplet reached by clicking a link is opened in a fresh top-level
    // window, so it gets a fresh localStorage partition and the FTUE
    // "already shown" flag isn't visible — the tour would re-appear on every
    // hop through a network of smaplets. The viewer has already seen it in the
    // originating smaplet, so tell the child to skip the auto tour (MAP-1535).
    const src = `${location.origin}/dashboard?mapId=${encodeURIComponent(mapId)}&noTour=1`;
    const title = `Smap ${name || 'Unnamed'}`;
    smapletWindow.document.write(
      `<style>html,body{margin:0;height:100%;}iframe{border:0;width:100%;height:100%;}</style>` +
      `<iframe sandbox="allow-scripts allow-same-origin allow-popups" allow="autoplay" src="${src}" title="${title}"></iframe>`
    );
    smapletWindow.document.title = title;
    smapletWindow.document.close();
  }

  /** Not publicly embeddable: open the smap in the application, as before. */
  private static fallbackToApp(smapletWindow: Window | null, url: string, linkTarget: LinkTarget) {
    if (smapletWindow) {
      smapletWindow.location.href = url;
    } else {
      DatatifyDirective.openWithTarget(url, linkTarget);
    }
  }

  constructor(private el: ElementRef,
              private settingsService: SettingsService,
              private renderer: Renderer2) {

  }

  ngAfterViewInit(): void {
    const text = this.el.nativeElement.textContent.trim();
    if (this.isValidUrl(text)) {
      const settings = this.settingsService.getLocalStorageSettings();
      // @ts-ignore
      let linkTarget = settings.linkTarget * 1 ?? LinkTarget.SingleWindow;
      if (isInIframe() && isNaN(linkTarget)) {
        linkTarget = LinkTarget.SingleWindow;
      }
      const link = this.renderer.createElement('a') as HTMLAnchorElement;
      link.style.wordBreak = 'break-all';
      link.style.wordWrap = 'break-word';
      this.renderer.setAttribute(link, 'href', '#');
      this.renderer.setAttribute(link, 'rel', 'noopener noreferrer');
      this.renderer.appendChild(link, this.renderer.createText(text));
      this.renderer.listen(link, 'click', (event) => DatatifyDirective.onLinkClick(event, text, linkTarget));
      this.renderer.insertBefore(this.el.nativeElement.parentNode, link, this.el.nativeElement);
      this.renderer.removeChild(this.el.nativeElement.parentNode, this.el.nativeElement);
    }
  }

  private isValidUrl(value: string): boolean {
    const urlRegex = /^(https?:\/\/).+\..+$/;
    return urlRegex.test(value);
  }


}
